import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import type { User } from "../types";
import { findAccount } from "../auth";
import { apiMode, apiUrl } from "../utils/api";

const SESSION_KEY = "tlart.session.v1";
const TOKEN_KEY = "tlart.token.v1";

/** Injected by esbuild at build time. */
interface AuthContextValue {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      return raw ? (JSON.parse(raw) as User) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try {
      if (user) localStorage.setItem(SESSION_KEY, JSON.stringify(user));
      else localStorage.removeItem(SESSION_KEY);
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, [user]);

  const login = useCallback(
    async (username: string, password: string): Promise<boolean> => {
      if (apiMode) {
        try {
          const res = await fetch(apiUrl("/api/auth/login"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username, password }),
          });
          if (!res.ok) return false;
          const { token, user: loggedIn } = (await res.json()) as {
            token: string;
            user: User;
          };
          localStorage.setItem(TOKEN_KEY, token);
          setUser(loggedIn);
          return true;
        } catch {
          return false;
        }
      }
      // localStorage / demo mode — verify against bundled accounts.
      const found = findAccount(username, password);
      if (found) {
        setUser(found);
        return true;
      }
      return false;
    },
    [],
  );

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem(TOKEN_KEY);
  }, []);

  const value = useMemo(() => ({ user, login, logout }), [user, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
